import type { TopDomain } from "./types";
import { domainColor } from "./styleHelpers";
import { DOMAIN_LABELS_AR } from "./constants";

interface TopDomainsBadgesProps {
  topDomains: TopDomain[];
  isRTL: boolean;
  /** Compact mode is used inside the LinkedIn sidebar panel. */
  compact?: boolean;
}

/** Displays the user's top 2 CliftonStrengths domains with rank and count. */
export default function TopDomainsBadges({
  topDomains,
  isRTL,
  compact = false,
}: TopDomainsBadgesProps) {
  const top2 = topDomains.slice(0, 2);

  if (compact) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8, direction: isRTL ? "rtl" : "ltr" }}>
        <div
          style={{
            fontSize: 9,
            letterSpacing: 2,
            color: "rgba(255,255,255,0.3)",
            textTransform: "uppercase",
            marginBottom: 2,
          }}
        >
          {isRTL ? "أبرز المجالات" : "Domain Leaders"}
        </div>

        {top2.map(({ domain, count }, idx) => {
          const color = domainColor(domain);
          const label = isRTL ? (DOMAIN_LABELS_AR[domain] ?? domain) : domain;
          return (
            <div key={domain} style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  background: color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <span style={{ fontSize: 11, fontWeight: 800, color: "#fff", fontFamily: "sans-serif" }}>
                  #{idx + 1}
                </span>
              </div>
              <div style={{ flex: 1, overflow: "hidden" }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#fff",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {label}
                </div>
                <div style={{ fontSize: 9, color, fontWeight: 600 }}>
                  {isRTL ? `${count} نقاط` : `${count} strengths`}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // ── Large variant for Instagram cards ──
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, direction: isRTL ? "rtl" : "ltr" }}>
      <div
        style={{
          fontSize: 11,
          letterSpacing: 2.5,
          color: "rgba(255,255,255,0.38)",
          textTransform: "uppercase",
          textAlign: "center",
        }}
      >
        {isRTL ? "أبرز المجالات" : "Domain Leaders"}
      </div>

      <div style={{ display: "flex", gap: 18 }}>
        {top2.map(({ domain, count }, idx) => {
          const color = domainColor(domain);
          const label = isRTL ? (DOMAIN_LABELS_AR[domain] ?? domain) : domain;
          const rankLabel = idx === 0
            ? (isRTL ? "الأول" : "Primary")
            : (isRTL ? "الثاني" : "Secondary");

          return (
            <div
              key={domain}
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                gap: 18,
                background: `${color}18`,
                border: `1.5px solid ${color}44`,
                borderRadius: 20,
                padding: "16px 22px",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* ambient glow */}
              <div
                style={{
                  position: "absolute",
                  top: -18,
                  right: -18,
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  background: color,
                  opacity: 0.18,
                  filter: "blur(18px)",
                }}
              />
              {/* rank badge */}
              <div
                style={{
                  width: 54,
                  height: 54,
                  borderRadius: 14,
                  background: color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  zIndex: 1,
                }}
              >
                <span style={{ fontSize: 20, fontWeight: 800, color: "#fff", fontFamily: "sans-serif" }}>
                  #{idx + 1}
                </span>
              </div>
              <div style={{ zIndex: 1, flex: 1, overflow: "hidden" }}>
                <div
                  style={{
                    fontSize: 10,
                    color: "rgba(255,255,255,0.45)",
                    fontWeight: 600,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    marginBottom: 4,
                  }}
                >
                  {rankLabel}
                </div>
                <div
                  style={{
                    fontSize: 19,
                    fontWeight: 700,
                    color: "#fff",
                    lineHeight: 1.2,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {label}
                </div>
                <div style={{ fontSize: 12, color, fontWeight: 600, marginTop: 3 }}>
                  {isRTL ? `${count} نقاط قوة` : `${count} strengths`}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
